<template>
  <div class="bg-pink-lighter pl-6 pt-3 pb-3">
    <div v-for="group in groups" :key="group.consumer.consumer_key">
      <ConsumerProgram
        :consumer="group.consumer"
        :programs="group.programs"
        :customer="customer"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import {ProgramSnapshot} from "@/program/interfaces/Program";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import Customer from "@/customer/interfaces/Customer";
import ConsumerProgram from "@/consumer/components/consumer-programs/ConsumerProgram.vue";

export interface ProgramWithConsumer extends ProgramSnapshot {
    consumer: Beneficiary | Customer;
}

export interface ConsumerProgramGroup {
    consumer: Beneficiary | Customer;
    programs: ProgramWithConsumer[];
}

export default defineComponent({
    components: {ConsumerProgram},
    props: {
        /**
         * The consumer that the programs belong to
         */
        consumer: {
            type: Object as PropType<Beneficiary | Customer>,
            required: true
        },
        /**
         * The customer that the consumer belongs to
         */
        customer: {
            type: Object as PropType<Customer>,
            required: true
        },
        /**
         * The consumers that the programs belong to, in order to display merged programs
         */
        consumers: {
            type: Array<Beneficiary | Customer>,
            required: false,
            default: []
        },
    },
    computed: {
        /**
         * The programs of every consumer, grouped by consumer, in the given order.
         *
         * The consumer the view is about keeps its group even when it holds no program, so that
         * its actions stay reachable; the other consumers only appear once they have one.
         */
        groups(): ConsumerProgramGroup[] {
            const consumers = this.consumers.length ? this.consumers : [this.consumer];

            return consumers
                .map(consumer => ({
                    consumer,
                    programs: consumer.programs
                        .map(program => ({
                            ...program,
                            consumer
                        }))
                        .filter(program => program.programmable_type !== 'feature')
                }))
                .filter(group =>
                    group.programs.length || group.consumer.consumer_key === this.consumer.consumer_key
                );
        }
    }
});
</script>
